import {
  coreExtensionData,
  createExtensionDataRef,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { EntityRefLinks } from '@backstage/plugin-catalog-react';
import catalogPlugin, {
  CatalogIndexPage,
  type CatalogTableColumnsFunc,
  type CatalogTableRow,
} from '@backstage/plugin-catalog/alpha';
import { CatalogTable } from '@backstage/plugin-catalog';
import type { TableColumn } from '@backstage/core-components';
import { getAnnotatedOwnerRefs } from 'backstage-plugin-simcorp';

// Same id as the (unexported) ref used by the catalog plugin's export config input.
const catalogExportConfigDataRef = createExtensionDataRef<any>().with({
  id: 'catalog.export-customization',
});

const ownersText = ({ entity }: CatalogTableRow) =>
  getAnnotatedOwnerRefs(entity).map(r => stringifyEntityRef(r)).join(', ');

const annotatedOwnerColumn: TableColumn<CatalogTableRow> = {
  ...CatalogTable.columns.createOwnerColumn(),
  field: 'entity.metadata.annotations.simcorp.com/owners',
  customFilterAndSearch: (query, row) =>
    ownersText(row).toLocaleUpperCase().includes(query.toLocaleUpperCase()),
  customSort: (a, b) => ownersText(a).localeCompare(ownersText(b)),
  render: ({ entity }) => (
    <EntityRefLinks
      entityRefs={getAnnotatedOwnerRefs(entity)}
      defaultKind="user"
    />
  ),
};

/** Replaces the owner column with the owners annotation, for systems only. */
const columnsFunc: CatalogTableColumnsFunc = context => {
  const columns = CatalogTable.defaultColumnsFunc(context);
  const kind =
    context.entities[0]?.kind?.toLowerCase() ?? context.filters.kind?.value;
  if (kind?.toLowerCase() !== 'system') {
    return columns;
  }
  return columns.map(c =>
    c.field === 'resolved.ownedByRelationsTitle' ? annotatedOwnerColumn : c,
  );
};

/**
 * Overrides the catalog index page to customize the table columns. The
 * extension factory mirrors the original, only the rendered page differs.
 */
export const catalogModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    catalogPlugin.getExtension('page:catalog').override({
      factory(originalFactory, { inputs, config }) {
        return originalFactory({
          params: {
            loader: async () => {
              const filters = inputs.filters.map(f =>
                f.get(coreExtensionData.reactElement),
              );
              const exportSettings: any = { ...config.exportSettings };
              for (const input of inputs.exportConfig) {
                const data = (input as any).get(catalogExportConfigDataRef);
                if (!data) continue;
                if (data.exporters) {
                  exportSettings.exporters = {
                    ...exportSettings.exporters,
                    ...data.exporters,
                  };
                }
                if (data.columns && !exportSettings.columns) {
                  exportSettings.columns = data.columns;
                }
                if (data.onSuccess && !exportSettings.onSuccess) {
                  exportSettings.onSuccess = data.onSuccess;
                }
                if (data.onError && !exportSettings.onError) {
                  exportSettings.onError = data.onError;
                }
              }
              return (
                <CatalogIndexPage
                  filters={<>{filters}</>}
                  columns={columnsFunc}
                  pagination={config.pagination}
                  exportSettings={
                    exportSettings.enabled ? exportSettings : undefined
                  }
                />
              );
            },
          },
        });
      },
    }),
  ],
});
